import type { LogEntry, LogLevel, Logger } from './logger.types';

const LEVEL_TO_CONSOLE_METHOD: Record<LogLevel, 'debug' | 'info' | 'warn' | 'error'> = {
  debug: 'debug',
  info: 'info',
  warning: 'warn',
  error: 'error',
  critical: 'error'
};

/**
 * Structured console logger — the default production implementation in this phase.
 * Emits JSON-structured entries so that a future provider (Sentry/OpenTelemetry,
 * per docs/architecture/03-monitoring-observability.md) can be swapped in behind
 * the same `Logger` interface without touching call sites.
 */
export class ConsoleLogger implements Logger {
  constructor(private readonly requestId?: string) {}

  private emit(level: LogLevel, message: string, context?: Record<string, unknown>): void {
    const entry: LogEntry = {
      level,
      message,
      requestId: this.requestId,
      context,
      timestamp: new Date().toISOString()
    };
    const method = LEVEL_TO_CONSOLE_METHOD[level];
    // eslint-disable-next-line no-console
    console[method](JSON.stringify(entry));
  }

  public debug(message: string, context?: Record<string, unknown>): void {
    this.emit('debug', message, context);
  }

  public info(message: string, context?: Record<string, unknown>): void {
    this.emit('info', message, context);
  }

  public warning(message: string, context?: Record<string, unknown>): void {
    this.emit('warning', message, context);
  }

  public error(message: string, context?: Record<string, unknown>): void {
    this.emit('error', message, context);
  }

  public critical(message: string, context?: Record<string, unknown>): void {
    this.emit('critical', message, context);
  }
}
